import React, { useEffect, useState, useRef } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    CircularProgress,
    Alert
} from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ReceiptIcon from '@mui/icons-material/Receipt';
import PrintIcon from '@mui/icons-material/Print';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { downloadReceiptPdf as fetchReceiptPdf } from '../../services/project';

// --- HELPER 1: Number to Words Converter ---
const numberToWords = (num) => {
    if (!num) return 'Zero';
    let numStr = num.toString().split('.')[0].replace(/[^\d]/g, '');
    if (!numStr) return 'Zero';
    let val = Number(numStr);
    if (val === 0) return 'Zero';

    const a = ['', 'One ', 'Two ', 'Three ', 'Four ', 'Five ', 'Six ', 'Seven ', 'Eight ', 'Nine ', 'Ten ', 'Eleven ', 'Twelve ', 'Thirteen ', 'Fourteen ', 'Fifteen ', 'Sixteen ', 'Seventeen ', 'Eighteen ', 'Nineteen '];
    const b = ['', '', 'Twenty ', 'Thirty ', 'Forty ', 'Fifty ', 'Sixty ', 'Seventy ', 'Eighty ', 'Ninety '];

    const convertTens = (n) => {
        if (n < 20) return a[n];
        return b[Math.floor(n / 10)] + a[n % 10];
    };

    const convertHundreds = (n) => {
        if (n > 99) {
            return a[Math.floor(n / 100)] + 'Hundred ' + convertTens(n % 100);
        } else {
            return convertTens(n);
        }
    };

    const convertHuge = (n) => {
        let str = '';
        if (n >= 10000000) {
            str += convertHuge(Math.floor(n / 10000000)).trim() + ' Crore ';
            n %= 10000000;
        }
        if (n >= 100000) {
            str += convertHundreds(Math.floor(n / 100000)).trim() + ' Lakh ';
            n %= 100000;
        }
        if (n >= 1000) {
            str += convertHundreds(Math.floor(n / 1000)).trim() + ' Thousand ';
            n %= 1000;
        }
        if (n > 0) {
            if (str !== '' && n < 100) {
                str += 'and ' + convertTens(n);
            } else {
                str += convertHundreds(n);
            }
        }
        return str;
    };

    let result = convertHuge(val);
    return result.replace(/\s+/g, ' ').trim() + ' Only';
};

// --- HELPER 2: Image to Base64 ---
const getBase64ImageFromUrl = async (imageUrl) => {
    if (!imageUrl) return null;
    try {
        const res = await fetch(imageUrl);
        const blob = await res.blob();
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
        });
    } catch (error) {
        console.warn('Image loading failed', error);
        return null;
    }
};

const triggerPdfDownload = (blob, fileName) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
};

const openPdfForPrint = (blob) => new Promise((resolve, reject) => {
    const url = URL.createObjectURL(blob);
    const printWindow = window.open(url, '_blank');

    if (!printWindow) {
        URL.revokeObjectURL(url);
        reject(new Error('Please allow popups to print the receipt.'));
        return;
    }

    let handled = false;
    const cleanup = () => {
        if (handled) return;
        handled = true;
        setTimeout(() => URL.revokeObjectURL(url), 60000);
        resolve();
    };

    const triggerPrint = () => {
        try {
            printWindow.focus();
            printWindow.print();
        } catch (error) {
            console.warn('Print window blocked', error);
        } finally {
            cleanup();
        }
    };

    printWindow.onload = () => setTimeout(triggerPrint, 300);
    setTimeout(triggerPrint, 1200);
});

const BookingDetailsPDF = ({
    open,
    onClose,
    type = 'list',
    data = null,
    extraData = null,
    autoPrint = false,
}) => {

    const [companyData, setCompanyData] = useState(null);
    const [loadingData, setLoadingData] = useState(false);
    const [generatingPdf, setGeneratingPdf] = useState(false);
    const [error, setError] = useState('');
    const autoPrintTriggeredRef = useRef(false);

    const isReceipt = type === 'receipt';

    useEffect(() => {
        if (!open) {
            setLoadingData(false);
            setError('');
            setCompanyData(null);
            autoPrintTriggeredRef.current = false;
            return;
        }

        if (isReceipt) {
            setLoadingData(false);
            setError('');
            setCompanyData(null);
        } else {
            fetchCompanyDetails();
        }
    }, [open, isReceipt]);

    const fetchCompanyDetails = async () => {
        setLoadingData(true);
        setError('');
        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/company/details`, {
                method: 'GET',
                headers: { 'Content-Type': 'application/json' },
            });

            if (!response.ok) throw new Error('Network response was not ok');
            const result = await response.json();

            if (result.success && result.data) {
                setCompanyData(result.data);
            } else {
                throw new Error('Invalid data format received');
            }
        } catch (err) {
            console.error('Failed to fetch company details', err);
            setError('Could not load company details.');
        } finally {
            setLoadingData(false);
        }
    };

    // --- GENERATE LIST (Existing Logic) ---
    const generateListPDF = async () => {
        setGeneratingPdf(true);
        try {
            const doc = new jsPDF({ orientation: 'landscape' });
            const logoBase64 = await getBase64ImageFromUrl('/logoonly.png');

            if (logoBase64) {
                const logoSize = 25;
                const pageWidth = doc.internal.pageSize.getWidth();
                doc.addImage(logoBase64, 'PNG', (pageWidth / 2) - (logoSize / 2), 8, logoSize, logoSize);

                doc.setFontSize(18);
                doc.setTextColor(40, 53, 147);
                doc.text(companyData.name || 'Company Name', pageWidth / 2, 38, { align: 'center' });
            } else {
                doc.setFontSize(18);
                doc.setTextColor(40, 53, 147);
                doc.text(companyData.name || 'Company Name', 14, 18);
            }

            const tableRows = Array.isArray(data) ? data.map(row => [
                row.bookingId, row.plotBookingDate, row.customer,
                row.address || 'N/A', // Added Address
                `${row.inventoryType || ''} ${row.plotNo}`, row.branchName,
                row.paymentPlan, row.plotAmount, row.discountAmount || 0, row.paidAmount, row.status
            ]) : [];

            autoTable(doc, {
                startY: logoBase64 ? 45 : 30,
                head: [['ID', 'Date', 'Customer', 'Address', 'Property', 'Branch', 'Plan', 'Total', 'Discount', 'Paid', 'Status']],
                body: tableRows,
            });

            doc.save('Booking_List.pdf');
        } catch (err) {
            alert('Error generating List');
        } finally {
            setGeneratingPdf(false);
        }
    };

    const getReceiptFileName = (receipt) => {
        const receiptId = receipt?.receiptId || (receipt?._id ? `TXN-${String(receipt._id).slice(-6).toUpperCase()}` : 'receipt');
        const customerName =
            extraData?._original?.customer?.name ||
            extraData?.customer?.name ||
            data?.customerName ||
            'Customer';

        const safeCustomerName = String(customerName).trim().split(' ')[0] || 'Customer';
        return `${safeCustomerName}_Receipt_${receiptId}.pdf`;
    };

    const generateReceiptPDF = async () => {
        if (!data?._id) {
            setError('Receipt ID not found.');
            return;
        }

        setGeneratingPdf(true);
        setError('');

        try {
            const blob = await fetchReceiptPdf(data._id);
            triggerPdfDownload(blob, getReceiptFileName(data));
        } catch (err) {
            console.error(err);
            setError(err.message || 'Could not download receipt.');
        } finally {
            setGeneratingPdf(false);
        }
    };

    const printReceiptPDF = async () => {
        if (!data?._id) {
            setError('Receipt ID not found.');
            return;
        }

        setGeneratingPdf(true);
        setError('');

        try {
            const blob = await fetchReceiptPdf(data._id);
            await openPdfForPrint(blob);
        } catch (err) {
            console.error(err);
            setError(err.message || 'Could not print receipt.');
        } finally {
            setGeneratingPdf(false);
        }
    };

    const handleDownload = () => {
        if (generatingPdf) return;
        if (!isReceipt && !companyData) return;
        if (isReceipt) {
            generateReceiptPDF();
        } else {
            generateListPDF();
        }
    };

    const handlePrint = () => {
        if (!isReceipt || generatingPdf) return;
        printReceiptPDF();
    };

    useEffect(() => {
        if (open && autoPrint && isReceipt && !generatingPdf && !autoPrintTriggeredRef.current) {
            autoPrintTriggeredRef.current = true;
            printReceiptPDF();
        }
    }, [autoPrint, generatingPdf, isReceipt, open]);

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
                {isReceipt ? <ReceiptIcon color="primary" /> : <PictureAsPdfIcon color="error" />}
                {isReceipt ? 'Download Receipt' : 'Export List'}
            </DialogTitle>

            <DialogContent dividers>
                {loadingData && <Box textAlign="center" py={2}><CircularProgress /><Typography>Fetching Details...</Typography></Box>}

                {error && <Alert severity="error">{error}</Alert>}

                {!loadingData && !error && (isReceipt || companyData) && (
                    <Box display="flex" flexDirection="column" alignItems="center" py={3}>
                        <Typography variant="h6" gutterBottom>Ready to Generate</Typography>
                        <Button
                            variant="contained"
                            color={isReceipt ? "primary" : "error"}
                            size="large"
                            onClick={handleDownload}
                            disabled={generatingPdf}
                            startIcon={generatingPdf ? <CircularProgress size={20} color="inherit" /> : <PictureAsPdfIcon />}
                        >
                            {generatingPdf ? 'Generating...' : 'Download PDF'}
                        </Button>
                        {isReceipt && (
                            <Button
                                variant="outlined"
                                color="primary"
                                size="large"
                                onClick={handlePrint}
                                disabled={generatingPdf}
                                startIcon={generatingPdf ? <CircularProgress size={20} color="inherit" /> : <PrintIcon />}
                                sx={{ mt: 2 }}
                            >
                                {generatingPdf ? 'Preparing...' : 'Print Receipt'}
                            </Button>
                        )}
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} color="inherit">Close</Button>
            </DialogActions>
        </Dialog>
    );
};

export default BookingDetailsPDF;
